import React from 'react';
import clsx from 'clsx';

export type TableColumns<R> = Record<
  string,
  {
    label?: React.ReactNode;
    align?: 'left' | 'center' | 'right';
    className?: string;
    headingClassName?: string;
    render?: (fieldName: string, row: R, rowIndex: number) => React.ReactNode;
  }
>;

interface TableHeadProps<R extends Record<string, unknown>> {
  columns: TableColumns<R>;
  striped?: boolean;
  inverted?: boolean;
}

const TableHead = <R extends Record<string, unknown>>({
  columns,
  striped = false,
  inverted = false,
}: TableHeadProps<R>) => (
  <thead
    className={clsx(
      'border-b font-medium',
      inverted ? 'bg-pt-primary' : 'bg-white dark:bg-black',
    )}
  >
    <tr>
      {Object.keys(columns).map((columnName) => (
        <th
          key={`tableHead${columnName}`}
          scope="col"
          className={clsx(
            'px-6 py-4',
            !striped && 'first:pl-0 last:pr-0',
            columns[columnName].align === 'left' && 'text-left',
            columns[columnName].align === 'center' && 'text-center',
            columns[columnName].align === 'right' && 'text-right',
            columns[columnName].headingClassName,
            columns[columnName].className,
          )}
        >
          {columns[columnName]?.label}
        </th>
      ))}
    </tr>
  </thead>
);

export interface TableProps<R extends Record<string, unknown>> {
  className?: string;
  columns: TableColumns<R>;
  rows: Array<R>;
  expandedRows?: Array<R>;
  getExpandedContent?: (row: R) => React.ReactNode;
  fillWidth?: boolean;
  striped?: boolean;
  highlight?: boolean;
  inverted?: boolean;
  responsive?: boolean;
  rowClassName?: (row: R) => string;
}

const renderFieldValue = <R extends Record<string, unknown>>(
  columnName: string,
  row: R,
  rowIndex: number,
  columns: TableColumns<R>,
): React.ReactNode => {
  if (columns?.[columnName]?.render) {
    return columns[columnName].render?.(columnName, row, rowIndex);
  }

  switch (typeof row[columnName]) {
    case 'boolean':
      return row[columnName] ? 'Yes' : 'No';
    // TODO: Some more types....
    default:
      return row[columnName] as string;
  }
};

const MODULO_FOR_ODD_EVEN = 2;

export const Table = <R extends Record<string, unknown>>({
  className,
  columns,
  rows,
  expandedRows,
  getExpandedContent,
  fillWidth = true,
  striped = false,
  highlight = false,
  inverted = false,
  responsive = true,
  rowClassName,
}: TableProps<R>) => (
  <div className={clsx(responsive && 'overflow-x-auto', className)}>
    <table
      className={clsx(
        'text-left text-sm font-light',
        fillWidth && 'min-w-full',
        inverted ? 'bg-pt-primary text-white' : 'bg-white dark:bg-neutral-900',
        className,
      )}
    >
      <TableHead columns={columns} striped={striped} inverted={inverted} />

      <tbody>
        {rows.map((row, index) => (
          <React.Fragment key={index}>
            <tr
              key={`tableRow${index}`}
              className={clsx(
                'group/row border-b',
                striped && index % MODULO_FOR_ODD_EVEN
                  ? inverted
                    ? 'pt-bottle-green'
                    : 'bg-pt-aqua-squeeze dark:bg-neutral-700'
                  : inverted
                    ? 'bg-pt-primary'
                    : 'bg-white dark:bg-neutral-900',
                highlight && 'hover:bg-neutral-200',
                rowClassName?.(row),
              )}
            >
              {Object.keys(columns).map((columnName) => (
                <td
                  key={`tableCell${index}${columnName}`}
                  className={clsx(
                    'whitespace-nowrap px-6 py-4 align-middle',
                    !striped && 'first:pl-0 last:pr-0',
                    columns[columnName].align === 'center' && 'text-center',
                    columns[columnName].align === 'right' && 'text-right',
                    columns[columnName].className,
                  )}
                >
                  {renderFieldValue<R>(columnName, row, index, columns)}
                </td>
              ))}
            </tr>

            {getExpandedContent && expandedRows?.includes(row) && (
              <tr key={`tableRow${index}Expanded`}>
                <td colSpan={Object.keys(columns).length}>
                  {getExpandedContent(row)}
                </td>
              </tr>
            )}
          </React.Fragment>
        ))}
      </tbody>
    </table>
  </div>
);
